import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ChevronLeft, ChevronRight, LogIn, LogOut, CalendarOff, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, KpiCard, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmy, titleize, today } from "@/lib/format";

const STATUSES = ["present", "absent", "on_leave", "half_day", "holiday", "weekly_off"];
const TONE = {
  present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  absent: "bg-red-50 text-red-700 border-red-200",
  on_leave: "bg-amber-50 text-amber-800 border-amber-200",
  half_day: "bg-blue-50 text-blue-700 border-blue-200",
  holiday: "bg-slate-100 text-slate-600 border-slate-200",
  weekly_off: "bg-slate-100 text-slate-600 border-slate-200",
};
const shiftDay = (day, delta) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};
const nowTime = () => new Date().toTimeString().slice(0, 5);

export default function Attendance() {
  const { can } = useAuth();
  const [day, setDay] = useState(today());
  const [department, setDepartment] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [leaveFor, setLeaveFor] = useState(null);
  const [leaveForm, setLeaveForm] = useState({ status: "on_leave", leave_reason: "" });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ status: "present", check_in: "", check_out: "", leave_reason: "" });

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/attendance/day", {
        params: { day, department: department || undefined, status: statusFilter || undefined,
          q: q.trim() || undefined } });
      setData(res);
    } catch (e) { setError(apiError(e)); }
  }, [day, department, statusFilter, q]);

  useEffect(() => {
    if (!can("attendance.view")) return;
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, can, q]);

  if (!can("attendance.view")) return <NoAccess what="staff attendance" />;

  const mark = async (userId, payload) => {
    try {
      await api.post("/attendance/mark", { user_id: userId, day, ...payload });
      toast.success("Attendance saved.");
      load();
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };

  const exportFile = async (fmt) => {
    const d = new Date(`${day}T00:00:00`);
    try {
      const res = await api.get("/attendance/export", {
        params: { month: d.getMonth() + 1, year: d.getFullYear(), fmt }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `attendance-${day.slice(0, 7)}.${fmt}`;
      a.click();
      toast.success(`Attendance exported as ${fmt.toUpperCase()}.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} />;

  return (
    <div data-testid="attendance-page">
      <PageHeader overline="Administration" title="Staff attendance"
        subtitle="Pick a date and mark the whole team for that day. Every correction is written to the audit log."
        actions={can("attendance.export") && (
          <>
            <button onClick={() => exportFile("xlsx")} className={ghostBtnCls} data-testid="attendance-export-xlsx">
              <Download className="h-4 w-4" /> Excel
            </button>
            <button onClick={() => exportFile("csv")} className={ghostBtnCls} data-testid="attendance-export-csv">
              <Download className="h-4 w-4" /> CSV
            </button>
          </>
        )} />

      <div className="panel mb-6 p-5 no-print">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <span className="text-[12px] font-semibold uppercase tracking-wider text-slate-700">Attendance date</span>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setDay(shiftDay(day, -1))} data-testid="attendance-prev-day"
                className="rounded-lg border border-slate-300 p-2.5 hover:border-slate-900" aria-label="Previous day">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input type="date" value={day} data-testid="attendance-date-picker"
                onChange={(e) => setDay(e.target.value || today())}
                className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm" />
              <button onClick={() => setDay(shiftDay(day, 1))} data-testid="attendance-next-day"
                className="rounded-lg border border-slate-300 p-2.5 hover:border-slate-900" aria-label="Next day">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={() => setDay(today())} className={ghostBtnCls} data-testid="attendance-today-btn">Today</button>
            </div>
          </div>
          <Field label="Search staff">
            <input className={inputCls} value={q} data-testid="attendance-search-input"
              placeholder="Name or employee ID" onChange={(e) => setQ(e.target.value)} />
          </Field>
          <Field label="Department">
            <select className={inputCls} value={department} data-testid="attendance-department-select"
              onChange={(e) => setDepartment(e.target.value)}>
              <option value="">All departments</option>
              {(data?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={inputCls} value={statusFilter} data-testid="attendance-status-filter"
              onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{titleize(s)}</option>)}
            </select>
          </Field>
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Showing attendance for <span className="font-semibold text-slate-800">{dmy(day)}</span>
          {day > (data?.today || today()) && " — future date"}
        </p>
      </div>

      {!data ? <Loading label="Loading the day's attendance…" /> : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-8">
            <KpiCard label="Staff" value={data.summary.staff} onClick={() => setStatusFilter("")} testid="att-kpi-staff" />
            <KpiCard label="Present" value={data.summary.present} tone="success" onClick={() => setStatusFilter("present")} testid="att-kpi-present" />
            <KpiCard label="On leave" value={data.summary.on_leave} tone="warning" onClick={() => setStatusFilter("on_leave")} testid="att-kpi-leave" />
            <KpiCard label="Absent" value={data.summary.absent} tone="danger" onClick={() => setStatusFilter("absent")} testid="att-kpi-absent" />
            <KpiCard label="Not marked" value={data.summary.not_marked} onClick={() => setStatusFilter("")} testid="att-kpi-unmarked" />
          </div>

          <div className="panel overflow-hidden" data-testid="attendance-table">
            {data.rows.length === 0 ? (
              <EmptyState title="No staff found" subtitle="Adjust the filters or create staff members to start marking attendance." testid="attendance-empty" />
            ) : (
              <>
                {/* Desktop / tablet table — no horizontal scrolling needed */}
                <table className="hidden md:table w-full text-sm table-fixed">
                  <thead className="bg-slate-50 text-left">
                    <tr className="text-[11px] uppercase tracking-wider text-slate-600">
                      <th className="px-4 py-4 w-[19%]">Staff</th>
                      <th className="px-3 py-4 w-[12%]">Role</th>
                      <th className="px-3 py-4 w-[12%]">Department</th>
                      <th className="px-3 py-4 w-[9%]">Date</th>
                      <th className="px-3 py-4 w-[8%]">Check-in</th>
                      <th className="px-3 py-4 w-[8%]">Check-out</th>
                      <th className="px-3 py-4 w-[10%]">Status</th>
                      <th className="px-3 py-4 w-[10%]">Leave reason</th>
                      <th className="px-3 py-4 w-[12%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.rows.map((r) => (
                      <tr key={r.user_id} data-testid={`attendance-row-${r.user_id}`} className="align-top">
                        <td className="px-4 py-4">
                          <p className="font-semibold text-slate-900 truncate">{r.name}</p>
                          <p className="text-xs text-slate-500">{r.employee_id || "No ID"}</p>
                        </td>
                        <td className="px-3 py-4 text-slate-600">{titleize(r.role)}</td>
                        <td className="px-3 py-4 text-slate-600 truncate">{r.department || "—"}</td>
                        <td className="px-3 py-4 text-slate-600 whitespace-nowrap">{dmy(r.day)}</td>
                        <td className="px-3 py-4 font-mono-num text-slate-800" data-testid={`att-in-${r.user_id}`}>{r.record?.check_in || "—"}</td>
                        <td className="px-3 py-4 font-mono-num text-slate-800" data-testid={`att-out-${r.user_id}`}>{r.record?.check_out || "—"}</td>
                        <td className="px-3 py-4">
                          {r.record ? (
                            <span data-testid={`att-status-${r.user_id}`}
                              className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${TONE[r.record.status]}`}>
                              {titleize(r.record.status)}
                            </span>
                          ) : <span className="text-xs text-slate-400" data-testid={`att-status-${r.user_id}`}>Not marked</span>}
                        </td>
                        <td className="px-3 py-4 text-xs text-slate-600 break-words">{r.record?.leave_reason || "—"}</td>
                        <td className="px-3 py-4">
                          <RowActions row={r} can={can} mark={mark}
                            onLeave={() => { setLeaveFor(r); setLeaveForm({ status: "on_leave", leave_reason: "" }); }}
                            onEdit={() => { setEditFor(r); setEditForm({ status: r.record?.status || "present",
                              check_in: r.record?.check_in || "", check_out: r.record?.check_out || "",
                              leave_reason: r.record?.leave_reason || "" }); }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className="divide-y divide-slate-100 md:hidden">
                  {data.rows.map((r) => (
                    <div key={r.user_id} className="p-4" data-testid={`attendance-card-${r.user_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{r.name}</p>
                          <p className="text-xs text-slate-500">{titleize(r.role)} · {r.department || "—"}</p>
                        </div>
                        {r.record ? <StatusPill status={r.record.status} /> : <span className="text-xs text-slate-400">Not marked</span>}
                      </div>
                      <p className="mt-2 text-xs text-slate-600">
                        {dmy(r.day)} · In {r.record?.check_in || "—"} · Out {r.record?.check_out || "—"}
                      </p>
                      {r.record?.leave_reason && <p className="mt-1 text-xs text-amber-700">{r.record.leave_reason}</p>}
                      <div className="mt-3">
                        <RowActions row={r} can={can} mark={mark}
                          onLeave={() => { setLeaveFor(r); setLeaveForm({ status: "on_leave", leave_reason: "" }); }}
                          onEdit={() => { setEditFor(r); setEditForm({ status: r.record?.status || "present",
                            check_in: r.record?.check_in || "", check_out: r.record?.check_out || "",
                            leave_reason: r.record?.leave_reason || "" }); }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      <Modal open={Boolean(leaveFor)} onClose={() => setLeaveFor(null)} title="Mark leave"
        subtitle={leaveFor ? `${leaveFor.name} · ${dmy(day)}` : ""} testid="attendance-leave-modal">
        <form className="grid gap-5 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!leaveForm.leave_reason.trim()) { toast.error("Leave reason is required."); return; }
            if (await mark(leaveFor.user_id, { ...leaveForm, check_in: "", check_out: "" })) setLeaveFor(null);
          }}>
          <Field label="Leave type *">
            <select className={inputCls} value={leaveForm.status} data-testid="leave-type-select"
              onChange={(e) => setLeaveForm({ ...leaveForm, status: e.target.value })}>
              <option value="on_leave">On leave (full day)</option>
              <option value="half_day">Half day</option>
              <option value="holiday">Holiday</option>
              <option value="weekly_off">Weekly off</option>
            </select>
          </Field>
          <Field label="Reason *" className="sm:col-span-2">
            <textarea rows={3} required className={inputCls} value={leaveForm.leave_reason}
              data-testid="leave-reason-input" placeholder="Sick leave, casual leave, approved by…"
              onChange={(e) => setLeaveForm({ ...leaveForm, leave_reason: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="leave-submit-btn">Save leave</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(editFor)} onClose={() => setEditFor(null)} title="Correct attendance entry"
        subtitle={editFor ? `${editFor.name} · ${dmy(day)}` : ""} testid="attendance-edit-modal">
        <form className="grid gap-5 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (["on_leave", "half_day"].includes(editForm.status) && !editForm.leave_reason.trim()) {
              toast.error("Leave reason is required."); return;
            }
            const payload = ["on_leave", "half_day"].includes(editForm.status)
              ? editForm : { ...editForm, leave_reason: "" };
            if (await mark(editFor.user_id, payload)) setEditFor(null);
          }}>
          <Field label="Status *">
            <select className={inputCls} value={editForm.status} data-testid="attendance-status-select"
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{titleize(s)}</option>)}
            </select>
          </Field>
          <Field label="Check-in">
            <input type="time" className={inputCls} value={editForm.check_in} data-testid="attendance-checkin-input"
              onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })} />
          </Field>
          <Field label="Check-out">
            <input type="time" className={inputCls} value={editForm.check_out} data-testid="attendance-checkout-input"
              onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })} />
          </Field>
          <Field label="Leave reason">
            <input className={inputCls} value={editForm.leave_reason} data-testid="attendance-reason-input"
              onChange={(e) => setEditForm({ ...editForm, leave_reason: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="attendance-save-btn">Save entry</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const RowActions = ({ row, can, mark, onLeave, onEdit }) => {
  const rec = row.record;
  const small = "inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900";
  return (
    <div className="flex flex-wrap gap-2">
      {!rec?.check_in && can("attendance.checkin") && (
        <button onClick={() => mark(row.user_id, { status: "present", check_in: nowTime() })}
          className={small} data-testid={`checkin-${row.user_id}`}><LogIn className="h-3.5 w-3.5" /> Check in</button>
      )}
      {rec?.check_in && !rec?.check_out && can("attendance.checkout") && (
        <button onClick={() => mark(row.user_id, { status: rec.status, check_in: rec.check_in,
          check_out: nowTime(), leave_reason: rec.leave_reason || "" })}
          className={small} data-testid={`checkout-${row.user_id}`}><LogOut className="h-3.5 w-3.5" /> Check out</button>
      )}
      {can("attendance.leave") && (
        <button onClick={onLeave} className={small} data-testid={`leave-${row.user_id}`}>
          <CalendarOff className="h-3.5 w-3.5" /> Leave
        </button>
      )}
      {can("attendance.edit") && (
        <button onClick={onEdit} className={small} data-testid={`edit-attendance-${row.user_id}`}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      )}
    </div>
  );
};
